// src/encounter/services/use-prescription-orders.ts
// Prescription orders for the pre-encounter Active Medications block (PROD-1588). The A&P reads
// the orders ALONE, never this merge (lib/to-ap-active-medications.ts).
//
// Wire (Flutter `encounter_data_source_impl.dart:757` getEPrescription):
//
//   GET /api/EPriscription/GetPrescriptionOrderDetails?PatientId=&EncounterId=
//       → EPrescriptionModel[]  (Scrips.Patient EPriscriptionController:90)
//
// **Backend typo preserved**: `/api/EPriscription` — the .NET route carries the typo, the
// correct spelling 404s (same as use-drug-interactions.ts). DO NOT "fix".
//
// WHY THIS EXISTS (round-4 correction): the claim "GetPatientSummary doesn't carry the rich
// med feeds" was only HALF true. Traced against Flutter:
//   · Rx chip (`isOrder`) + Ordered/Added-by (`administration.performerDetails`) + start/end
//     dates DO ride GetPatientSummary (patient_medications.dart fromJson) — the React
//     PatientMedication type just hadn't captured them.
//   · Patient SIG / Dispense / Refills / Ordered-for (dx codes) do NOT — Flutter's
//     PatientMedications hardcodes `sig => ""`, `dispense/refills => 0`. Those fields live on
//     EPrescriptionModel, fed by THIS endpoint (Flutter FetchEPrescriptionEvent,
//     patient_encounter_bloc.dart:417/2382).
// So the React block merges both feeds, exactly as Flutter's data layer holds both
// (state.patientSummary + state.activeMedications, matched by medication code — see
// pre_encounter_widget.dart openMedicationUpdateDialog's description-based match).
//
// Taking / Not-taking (reconciliation writes): NO server endpoint exists.
// Scrips.Patient EPriscriptionController (origin/HEAD f68b66db, 2026-07-19) exposes only
// POST create · PUT Update/{patientRxOrderId} · PostRefill · PostStopMedication ·
// PostSuspensionMedication (+ reads). Flutter has no Taking/Not-taking flow either — its
// pre-encounter rows show TimeLine + Expand only (showEditOption=false,
// active_medication_row.dart). The DS row therefore renders WITHOUT the reconciliation
// actions until a status endpoint ships (anti-fiction).
import { useQuery } from '@tanstack/react-query';
import { get, ApiError } from '@/shared/api/api-client';
import { formatDate } from '@/shared/lib/date-format';
import type { PatientSummary } from '@/types/patient-summary-api';
import type { ActiveMedicationItem } from '../encounter-workflow';
import { durationLabel, medStatus, orderedFor, quantity, text } from '../lib/to-ap-active-medications';
import { medicationDoseLine } from '../lib/medication-dose-line';
import { clinicalDisplayName } from '@/lib/clinical-display-name';

/** Dx link on an Rx order (Flutter RxDiagnosisCodesRequest → dxCode). */
export interface PrescriptionDxLink {
  id?: string | null;
  isDeleted?: boolean | null;
  dxCode?: { code?: string | null; displayName?: string | null; display?: string | null } | null;
}

/** One prescription order — the subset of Flutter EPrescriptionModel.fromJson the block needs. */
export interface PrescriptionOrder {
  id?: string | null;
  orderId?: string | null;
  status?: string | null;
  /** Patient SIG free text — the field GetPatientSummary genuinely does not carry. */
  sig?: string | null;
  frequency?: string | null;
  duration?: string | null;
  durationType?: string | null;
  startDate?: string | null;
  createdDate?: string | null;
  performer?: string | null;
  performerDetails?: { id?: string | null; name?: string | null; roleName?: string | null } | null;
  isDeleted?: boolean | null;
  isOrder?: boolean | null;
  medicationRequest?: {
    code?: { code?: string | null; display?: string | null } | null;
  } | null;
  rxDiagnosisCodesRequest?: PrescriptionDxLink[] | null;
  dispenseRequest?: { quantityValue?: number | string | null; quantityUnit?: string | null } | null;
  refillRequest?: { quantityValue?: number | string | null } | null;
}

/**
 * Prescription orders for a patient + encounter. Enabled only when both ids resolve
 * (Flutter fires FetchEPrescriptionEvent only after the encounter exists — bloc:417).
 * 404 → [] (Flutter parity: "no orders" is a normal state, not an error).
 */
export function usePrescriptionOrders(
  patientId: string | null | undefined,
  encounterId: string | null | undefined,
) {
  return useQuery<PrescriptionOrder[]>({
    queryKey: ['prescription-orders', patientId, encounterId],
    enabled: !!patientId && !!encounterId,
    queryFn: async ({ signal }) => {
      try {
        return (
          (await get<PrescriptionOrder[]>(
            `/api/EPriscription/GetPrescriptionOrderDetails?PatientId=${encodeURIComponent(
              patientId as string,
            )}&EncounterId=${encodeURIComponent(encounterId as string)}`,
            { signal },
          )) ?? []
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return [];
        throw e;
      }
    },
  });
}

/**
 * Merge the GetPatientSummary medication list (base rows — Flutter's pre-encounter source,
 * pre_encounter_widget.dart activeMedication()) with GetPrescriptionOrderDetails orders
 * (SIG / dispense / refills / ordered-for). Match key = medication CODE, Flutter's own
 * summary↔order join (description-based match in openMedicationUpdateDialog); orderId is
 * used first when both sides carry it.
 */
export function toRichActiveMedications(
  summary: PatientSummary | undefined,
  orders: PrescriptionOrder[] | undefined,
): ActiveMedicationItem[] {
  const liveOrders = (orders ?? []).filter((o) => !o.isDeleted);
  const byOrderId = new Map<string, PrescriptionOrder>();
  const byCode = new Map<string, PrescriptionOrder>();
  for (const o of liveOrders) {
    // Later entries win — the API returns orders in creation order, so the latest Rx for a
    // code carries the current SIG.
    if (o.orderId) byOrderId.set(o.orderId, o);
    const code = text(o.medicationRequest?.code?.code);
    if (code) byCode.set(code, o);
  }

  return (summary?.medication ?? [])
    .filter((m) => !m.isDeleted)
    .map((m, i) => {
      const order =
        (m.orderId ? byOrderId.get(m.orderId) : undefined) ?? byCode.get(text(m.code?.code)) ?? undefined;
      const detail = medicationDoseLine(m);
      const sig = text(order?.sig);
      const admin = m.administration;
      const prescriber = text(order?.performerDetails?.name) || text(admin?.performerDetails?.name) || undefined;
      const isRx = Boolean(m.isOrder) || Boolean(order);
      const status = medStatus(m.status, m.frequency);
      return {
        id: m.identifier ?? `med-${i}`,
        name: clinicalDisplayName(m.code).name || 'Medication',
        detail,
        sig: sig || undefined,
        isRx,
        status,
        prescriber,
        orderedOn: order ? formatDate(order.createdDate ?? order.startDate) || undefined : undefined,
        orderedFor: order ? orderedFor(order) : undefined,
        startedOn: formatDate(admin?.startDate ?? m.createdDate) || undefined,
        endedOn: formatDate(admin?.endDate) || undefined,
        durationLabel: durationLabel(order),
        dispense: quantity(order?.dispenseRequest?.quantityValue),
        refills: quantity(order?.refillRequest?.quantityValue),
      } satisfies ActiveMedicationItem;
    });
}
